/**
 * Medication tracker: dose schedule, live countdowns, meal times and alarms.
 *
 * Replaces `renderPatientMedications`, `renderPatientMedicinesFromPrescriptions`,
 * `openAddMedicationModal`, `savePatientFullMedicine`, `startLiveCountdowns`,
 * `savePatientMealTimes`, `togglePatientPrescriptionMedicineAlarm`,
 * `updateMedicineFirstDoseTime` and `openFirstDoseModal`.
 *
 * The medicine list is patient-local (the backend has no medicine table), so it
 * stays in localStorage as before — but seeded from the prescriptions the doctor
 * actually wrote, which is what made the legacy screen useful.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Select,
  Spinner,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { usePatientPrescriptions } from '../../hooks/usePrescriptions';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useTicker } from '../../hooks/usePolling';
import { useMedicineAlarm } from '../../hooks/useMedicineAlarm';
import { MedicineAlarmModal } from './MedicineAlarmModal';
import {
  DEFAULT_MEAL_TIMES,
  MEAL_KEYS,
  MEAL_LABELS,
  buildDoseSchedule,
  courseDaysRemaining,
  isMealTimeMedicine,
  mealCountdown,
  nextDoseCountdown,
  nextDoseTime,
  nextMealLabel,
} from '../../domain/medication';

const FREQUENCY_OPTIONS = [
  { value: 1, label: 'مرة يومياً' },
  { value: 2, label: 'مرتين يومياً' },
  { value: 3, label: '3 مرات يومياً' },
  { value: 4, label: '4 مرات يومياً' },
  { value: 6, label: 'كل 4 ساعات' },
];

const emptyMedicine = () => ({
  id: String(Date.now()),
  name: '',
  dosage: '',
  frequency: 2,
  firstDoseTime: '08:00',
  durationDays: 7,
  startDate: new Date().toISOString().slice(0, 10),
  notes: '',
  alarmEnabled: true,
  source: 'manual',
});

export function PatientMedications() {
  const { patientId } = useAuth();
  const { success } = useNotifications();

  const storageKey = `capsule_patient_medicines_${patientId ?? 'anonymous'}`;
  const [medicines, setMedicines] = useLocalStorage(storageKey, []);
  const [mealTimes, setMealTimes] = useLocalStorage('capsule_admin_meal_times', DEFAULT_MEAL_TIMES);

  const { prescriptions, loading } = usePatientPrescriptions(patientId);
  const now = useTicker(true);

  const [editing, setEditing] = useState(null);
  const [mealModalOpen, setMealModalOpen] = useState(false);

  const alarm = useMedicineAlarm(medicines, { enabled: true });

  /** Medicines the doctor prescribed that are not yet in the tracker. */
  const suggestions = useMemo(() => {
    const known = new Set(medicines.map((m) => String(m.name).trim().toLowerCase()));
    const found = [];
    for (const prescription of prescriptions) {
      for (const medicine of prescription.medicines || []) {
        const name = String(medicine?.name || '').trim();
        if (!name || known.has(name.toLowerCase())) continue;
        known.add(name.toLowerCase());
        found.push({ ...medicine, prescriptionId: prescription.id, prescriptionDate: prescription.date });
      }
    }
    return found;
  }, [prescriptions, medicines]);

  const addFromPrescription = (medicine) => {
    setMedicines((current) => [
      ...current,
      {
        ...emptyMedicine(),
        id: `${Date.now()}-${current.length}`,
        name: medicine.name,
        dosage: medicine.dosage || medicine.dose || '',
        notes: medicine.notes || '',
        source: 'prescription',
        prescriptionId: medicine.prescriptionId,
      },
    ]);
    success(`تمت إضافة ${medicine.name} لقائمة أدويتك`);
  };

  const saveMedicine = (medicine) => {
    setMedicines((current) => {
      const exists = current.some((m) => m.id === medicine.id);
      return exists ? current.map((m) => (m.id === medicine.id ? medicine : m)) : [...current, medicine];
    });
    setEditing(null);
    success('تم حفظ الدواء');
  };

  const removeMedicine = (id) => setMedicines((current) => current.filter((m) => m.id !== id));

  const toggleAlarm = (id) =>
    setMedicines((current) =>
      current.map((m) => (m.id === id ? { ...m, alarmEnabled: !m.alarmEnabled } : m)),
    );

  return (
    <div className="space-y-6">
      <PageHeader
        title="أدويتي"
        subtitle={`${medicines.length} دواء — الوجبة القادمة: ${nextMealLabel(mealTimes, now)}`}
        icon="pill"
        actions={
          <>
            <Button variant="ghost" icon="utensils" onClick={() => setMealModalOpen(true)}>
              مواعيد الوجبات
            </Button>
            <Button icon="plus" onClick={() => setEditing(emptyMedicine())}>
              إضافة دواء
            </Button>
          </>
        }
      />

      {/* Prescribed but not tracked */}
      {suggestions.length > 0 && (
        <Panel className="border border-cyan-500/20">
          <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-cyan-300">
            <Icon name="sparkles" size={18} />
            أدوية من روشتاتك ({suggestions.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((medicine) => (
              <button
                key={`${medicine.prescriptionId}-${medicine.name}`}
                type="button"
                onClick={() => addFromPrescription(medicine)}
                className="flex items-center gap-2 rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-xs font-bold text-slate-200 transition-colors hover:border-cyan-500/50 hover:bg-slate-800"
              >
                <Icon name="plus" size={13} className="text-cyan-400" />
                {medicine.name}
              </button>
            ))}
          </div>
        </Panel>
      )}

      {loading && medicines.length === 0 && <Spinner variant="inline" label="جاري التحميل..." />}

      {medicines.length === 0 ? (
        <Panel>
          <EmptyState
            icon="pill"
            title="لا توجد أدوية مضافة"
            message="أضف أدويتك لتتبع الجرعات والحصول على تنبيهات في مواعيدها."
            action={
              <Button icon="plus" onClick={() => setEditing(emptyMedicine())}>
                إضافة أول دواء
              </Button>
            }
          />
        </Panel>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {medicines.map((medicine) => (
            <MedicineCard
              key={medicine.id}
              medicine={medicine}
              mealTimes={mealTimes}
              now={now}
              onEdit={() => setEditing(medicine)}
              onRemove={() => removeMedicine(medicine.id)}
              onToggleAlarm={() => toggleAlarm(medicine.id)}
            />
          ))}
        </div>
      )}

      <MedicineFormModal
        medicine={editing}
        onClose={() => setEditing(null)}
        onSave={saveMedicine}
      />

      <MealTimesModal
        open={mealModalOpen}
        onClose={() => setMealModalOpen(false)}
        mealTimes={mealTimes}
        onSave={(next) => {
          setMealTimes(next);
          setMealModalOpen(false);
          success('تم حفظ مواعيد الوجبات');
        }}
      />

      <MedicineAlarmModal
        alarm={alarm.activeAlarm}
        onDismiss={alarm.dismiss}
        onSnooze={alarm.snooze}
        onTaken={alarm.markTaken}
      />
    </div>
  );
}

/* ========================================================================== */

function MedicineCard({ medicine, mealTimes, now, onEdit, onRemove, onToggleAlarm }) {
  const mealBased = isMealTimeMedicine(medicine);
  const schedule = useMemo(() => buildDoseSchedule(medicine, now), [medicine, now]);
  const daysLeft = courseDaysRemaining(medicine, now);

  return (
    <Panel className="space-y-4">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-cyan-500/10 p-2.5 text-cyan-400">
          <Icon name="pill" size={20} />
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-bold text-white">{medicine.name}</h3>
          {medicine.dosage && <p className="text-xs text-slate-400">{medicine.dosage}</p>}
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {medicine.source === 'prescription' && (
              <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-300" icon="file-text">
                من روشتة
              </Badge>
            )}
            {daysLeft !== null && (
              <Badge
                className={
                  daysLeft <= 0
                    ? 'border-slate-600 bg-slate-800 text-slate-400'
                    : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                }
              >
                {daysLeft <= 0 ? 'انتهى الكورس' : `باقي ${daysLeft} يوم`}
              </Badge>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onToggleAlarm}
          className={`shrink-0 rounded-lg p-2 transition-colors ${
            medicine.alarmEnabled
              ? 'bg-amber-500/15 text-amber-300'
              : 'bg-slate-800 text-slate-500 hover:text-slate-300'
          }`}
          aria-label={medicine.alarmEnabled ? 'إيقاف التنبيه' : 'تفعيل التنبيه'}
          title={medicine.alarmEnabled ? 'التنبيه مفعل' : 'التنبيه متوقف'}
        >
          <Icon name={medicine.alarmEnabled ? 'bell-ring' : 'bell-off'} size={16} />
        </button>
      </div>

      {/* Countdown */}
      <div className="rounded-xl border border-white/5 bg-white/5 p-3 text-center">
        <p className="mb-1 text-[11px] text-slate-400">
          {mealBased ? 'الوجبة القادمة' : 'الجرعة القادمة'}
        </p>
        <p className="font-mono text-2xl font-bold text-cyan-300" dir="ltr">
          {mealBased ? mealCountdown(mealTimes, now) : nextDoseCountdown(medicine, now)}
        </p>
        <p className="mt-0.5 text-[11px] text-slate-500" dir="ltr">
          {mealBased ? nextMealLabel(mealTimes, now) : nextDoseTime(medicine, now)}
        </p>
      </div>

      {/* Today's doses */}
      {!mealBased && schedule.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {schedule.map((dose) => (
            <span
              key={dose.seconds}
              dir="ltr"
              className={`rounded border px-2 py-1 text-[10px] ${
                dose.isNext
                  ? 'animate-pulse border-teal-500/50 bg-teal-500/30 font-bold text-teal-200'
                  : dose.isPast
                    ? 'border-slate-600 bg-slate-700 text-slate-400'
                    : 'border-slate-500/30 bg-slate-600/50 text-slate-300'
              }`}
            >
              {dose.isNext ? '⏰ ' : ''}
              {dose.time}
            </span>
          ))}
        </div>
      )}

      {medicine.notes && <p className="text-xs leading-relaxed text-slate-400">{medicine.notes}</p>}

      <div className="flex justify-end gap-2 border-t border-white/5 pt-3">
        <Button variant="subtle" icon="pencil" onClick={onEdit}>
          تعديل
        </Button>
        <Button variant="subtle" icon="trash-2" onClick={onRemove} className="!text-red-300">
          حذف
        </Button>
      </div>
    </Panel>
  );
}

/* ========================================================================== */

function MedicineFormModal({ medicine, onClose, onSave }) {
  const [form, setForm] = useState(medicine);

  useEffect(() => setForm(medicine), [medicine]);

  if (!form) return null;

  const update = (field) => (event) => {
    const value = event.target.type === 'number' ? Number(event.target.value) : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={medicine?.name ? 'تعديل الدواء' : 'إضافة دواء'}
      icon="pill"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="save" onClick={() => onSave(form)} disabled={!form.name.trim()}>
            حفظ
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="اسم الدواء" required>
          <Input value={form.name} onChange={update('name')} placeholder="مثال: أوجمنتين 1جم" />
        </Field>

        <Field label="الجرعة" hint="مثال: قرص واحد، ملعقة، مع الوجبات">
          <Input value={form.dosage} onChange={update('dosage')} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="عدد المرات يومياً">
            <Select value={form.frequency} onChange={update('frequency')}>
              {FREQUENCY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="موعد أول جرعة">
            <Input type="time" value={form.firstDoseTime} onChange={update('firstDoseTime')} dir="ltr" />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="مدة الكورس (أيام)">
            <Input type="number" min="1" value={form.durationDays} onChange={update('durationDays')} />
          </Field>

          <Field label="تاريخ البدء">
            <Input type="date" value={form.startDate} onChange={update('startDate')} dir="ltr" />
          </Field>
        </div>

        <Field label="ملاحظات">
          <Input value={form.notes} onChange={update('notes')} />
        </Field>

        <Checkbox
          label="تفعيل التنبيه الصوتي عند موعد الجرعة"
          checked={form.alarmEnabled}
          onChange={(event) => setForm((current) => ({ ...current, alarmEnabled: event.target.checked }))}
        />
      </div>
    </Modal>
  );
}

/* ========================================================================== */

function MealTimesModal({ open, onClose, mealTimes, onSave }) {
  const [form, setForm] = useState(mealTimes);

  useEffect(() => setForm(mealTimes), [mealTimes, open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="مواعيد الوجبات"
      icon="utensils"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="save" onClick={() => onSave(form)}>
            حفظ
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-slate-400">
          تُستخدم هذه المواعيد لحساب توقيت الأدوية المكتوبة &quot;مع الوجبات&quot;.
        </p>
        {MEAL_KEYS.map((key) => (
          <Field key={key} label={MEAL_LABELS[key]}>
            <Input
              type="time"
              dir="ltr"
              value={form?.[key] || ''}
              onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
            />
          </Field>
        ))}
      </div>
    </Modal>
  );
}

export default PatientMedications;
